import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../CustomerDueSlice";
import { fetchCustomerDue } from "../CustomerDueThunk";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  fetchCustomers,
  fetchLocations,
  fetchTagGroups,
  fetchTagGroupFields,
} from "../../../store/commonThunks/lookups";

const CustomerDueFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.customerDue);
  const lookups = useSelector((state) => state.lookups);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // dispatch(fetchAccounts({ type: "debitor" }));
    dispatch(fetchCustomers({ type: "debitor" }));
    dispatch(fetchLocations());
    dispatch(fetchTagGroups());
  }, [dispatch]);

  // Drill-down from Balance Sheet: /reports/debitors-balance/<toDate>?fromDate=<fromDate>
  useEffect(() => {
    const path = pathname.split("/")[3];
    if (path) {
      const fromDate = searchParams.get("fromDate") || null;
      const nextFilters = { ...filters, fromDate, toDate: path };
      dispatch(setFilters({ fromDate, toDate: path }));
      dispatch(
        fetchCustomerDue({
          selectedFilterType: nextFilters.filterType,
          filters: nextFilters,
        })
      );
    }
  }, []);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      filters,
    };

    dispatch(fetchCustomerDue(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const renderExtraField = () => {
    switch (filters.filterType) {
      case "Customer Wise":
        return (
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <label>Search Customer</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups?.customers
                ?.filter(acc => acc.acc_type_name !== "Insurance")   
                ?.map(acc => ({
                  sale_id: acc.acc_id,
                  display_text: acc.acc_name,
                }))
              }
              onSelect={(obj) => handleFilterChange("customerId", obj?.sale_id)}
              placeholder="Search Customer"
              showAddNew={false}
            />
          </Col>
        );

      case "By Tag":
        return (
          <>
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <label>Tag Group</label>
              <SearchListAutoComplete
                optionsList={lookups.tagGroups.map((tg) => ({
                  sale_id: tg.id,
                  display_text: tg.tag_groupname,
                }))}
                onSelect={(obj) => {
                  handleFilterChange("tagGroupId", obj?.sale_id);
                  if (obj?.sale_id) dispatch(fetchTagGroupFields(obj.sale_id));
                }}
                placeholder="Tag Group"
                showAddNew={false}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <label>Tag Field</label>
              <SearchListAutoComplete
                optionsList={lookups.tagFields.map((tf) => ({
                  sale_id: tf.id,
                  display_text: tf.display_text || tf.combinedField,
                }))}
                onSelect={(obj) =>
                  handleFilterChange("tagFieldId", obj?.sale_id)
                }
                placeholder="Tag Field"
                showAddNew={false}
              />
            </Col>
          </>
        );

      case "Location Wise":
        return (
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <label>Select Location</label>
            <SearchListAutoComplete
              optionsList={lookups.locations.map((loc) => ({
                sale_id: loc.location_id,
                display_text: loc.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Select Location"
              showAddNew={false}
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All Customer", display_text: "All Customer" },
              { sale_id: "Due List", display_text: "Due List" },
              { sale_id: "Customer Wise", display_text: "Customer Wise" },
              { sale_id: "By Tag", display_text: "By Tag" },
              { sale_id: "Location Wise", display_text: "Location Wise" },
            ]}
            value={{ value: filters.filterType, label: filters.filterType }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        <Col
          xs={24}
          sm={12}
          md={8}
          lg={6}
          xl={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            data-testid="btn-get-report"
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default CustomerDueFilters;
